/**
 * Zone-Based Data Source Manager
 * 
 * Manages data sources by geographic/political zones (USA, Canada, International)
 * Similar to industry-news-zones.js pattern from GovtTenderAI
 */

import { DataSourceType } from './dataSourceRegistry';

// India & Foreign portal source IDs (used in zone and filters)
export const INDIA_AND_FOREIGN_PORTAL_SOURCES: DataSourceType[] = [
  'tender_247', 'tendertiger_india', 'eprocure', 'gem', 'hp_tenders', 'tender_odisha', 'uk_tenders',
  'tendertiger_brazil', 'tendertiger_canada', 'tendertiger_ethiopia', 'tendertiger_indonesia',
  'tendertiger_kenya', 'tendertiger_mexico', 'tendertiger_peru', 'tendertiger_south_africa',
  'tendertiger_tanzania', 'tendertiger_usa',
];

export type Zone = 'usa' | 'canada' | 'international' | 'europe' | 'india_foreign';

export interface ZoneDefinition {
  id: Zone;
  name: string;
  icon: string;
  description: string;
  dataSources: DataSourceType[];
}

export const ZONE_DEFINITIONS: Record<Zone, ZoneDefinition> = {
  usa: {
    id: 'usa',
    name: 'USA',
    icon: '🇺🇸',
    description: 'U.S. Federal spending data, contracts, awards, Executive Orders, and policy updates',
    dataSources: [
      'samgov',
      'usa_awards',
      'usa_budgets',
      'federal_register',
      'executive_orders',
      'rss_feeds',
    ],
  },
  canada: {
    id: 'canada',
    name: 'Canada',
    icon: '🇨🇦',
    description: 'Canadian grants, contributions, tenders, and funded projects',
    dataSources: [
      'canada_grants',
      'canada_tenders',
      'bc_bid',
      'bids_tender',
      'bonfire',
      'merx',
      'vancouver_city_portal',
      'victoria_city_portal',
      'metrovancouver_city_portal',
    ],
  },
  international: {
    id: 'international',
    name: 'International',
    icon: '🌍',
    description: 'World Bank, UN, and other international opportunities',
    dataSources: [
      'worldbank',
      'gbof',
    ],
  },
  europe: {
    id: 'europe',
    name: 'Europe',
    icon: '🇪🇺',
    description: 'European Union public procurement and tenders',
    dataSources: [], // Future: TED, EU funding
  },
  india_foreign: {
    id: 'india_foreign',
    name: 'India & Foreign',
    icon: '🌐',
    description: 'India and foreign country tender portals (Tender247, TenderTiger, eProcure, GEM, HP Tenders, etc.)',
    dataSources: INDIA_AND_FOREIGN_PORTAL_SOURCES,
  },
};

export class ZoneManager {
  private currentZone: Zone = 'usa';
  private listeners: Array<(newZone: Zone, oldZone: Zone) => void> = [];

  constructor() {
    // Load saved zone preference
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('rfp-agent-zone');
      if (saved && saved in ZONE_DEFINITIONS) {
        this.currentZone = saved as Zone;
      }
    }
  }

  getCurrentZone(): Zone {
    return this.currentZone;
  }

  getZoneDefinition(zone: Zone): ZoneDefinition {
    return ZONE_DEFINITIONS[zone];
  }

  getAllZones(): ZoneDefinition[] {
    return Object.values(ZONE_DEFINITIONS);
  }

  switchZone(newZone: Zone): void {
    if (newZone === this.currentZone) return;
    if (!(newZone in ZONE_DEFINITIONS)) {
      console.warn(`Unknown zone: ${newZone}`);
      return;
    }

    const oldZone = this.currentZone;
    this.currentZone = newZone;

    // Save preference
    if (typeof window !== 'undefined') {
      localStorage.setItem('rfp-agent-zone', newZone);
    }

    // Notify listeners
    this.listeners.forEach(listener => listener(newZone, oldZone));
  }

  addListener(listener: (newZone: Zone, oldZone: Zone) => void): void {
    this.listeners.push(listener);
  }

  removeListener(listener: (newZone: Zone, oldZone: Zone) => void): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  getDataSourcesForZone(zone: Zone): DataSourceType[] {
    return ZONE_DEFINITIONS[zone]?.dataSources || [];
  }

  getDataSourcesForCurrentZone(): DataSourceType[] {
    return this.getDataSourcesForZone(this.currentZone);
  }
}

// Singleton instance
let zoneManagerInstance: ZoneManager | null = null;

export function getZoneManager(): ZoneManager {
  if (!zoneManagerInstance) {
    zoneManagerInstance = new ZoneManager();
  }
  return zoneManagerInstance;
}










